import { Component, OnInit } from '@angular/core';
import { OrderProfitService } from '../../../../services/order-profit';
import { ItemsService } from '../../../../services/items.service';
import { TranslocoService } from '@ngneat/transloco';

interface OrderProfitRow {
  id?: number | string;
  order_id?: number | string;
  order_number?: number | string;
  created_at?: string;
  customer_name?: string;
  client_name?: string;
  username?: string;
  total?: number | string;
  total_amount?: number | string;
  selling_price?: number | string;
  revenue?: number | string;
  cost?: number | string;
  cost_price?: number | string;
  total_cost?: number | string;
  profit?: number | string;
  net_profit?: number | string;
  user_id?: number | string;
  gross_sales?: number | string;
  products_cost?: number | string;
  invoice_discount?: number | string;
  partial_deduction?: number | string;
  other_deductions?: number | string;
  items_count?: number | string;
  total_quantity?: number | string;
  [key: string]: unknown;
}

@Component({
  selector: 'app-ordersprofit',
  templateUrl: './ordersprofit.component.html',
  styleUrl: './ordersprofit.component.scss'
})
export class OrdersprofitComponent implements OnInit {
  readonly isAr = localStorage.getItem('language') === 'ar';
  warehouseId = Number(localStorage.getItem('posId')) || 0;
  profits: OrderProfitRow[] = [];
  loading = false;
  syncing = false;
  errorMessage = '';
  currentPage = 1;
  lastPage = 1;
  perPage = 10;
  totalRecords = 0;
  totalRevenue = 0;
  totalCost = 0;
  netProfit = 0;
  warehouses: any[] = [];
  selectedYear = new Date().getFullYear();
  selectedMonth = new Date().getMonth() + 1;
  readonly years = Array.from({ length: 8 }, (_, index) => new Date().getFullYear() - index);
  readonly months = [
    { value: 1, key: 'January' }, { value: 2, key: 'February' }, { value: 3, key: 'March' },
    { value: 4, key: 'April' }, { value: 5, key: 'May' }, { value: 6, key: 'June' },
    { value: 7, key: 'July' }, { value: 8, key: 'August' }, { value: 9, key: 'September' },
    { value: 10, key: 'October' }, { value: 11, key: 'November' }, { value: 12, key: 'December' }
  ];

  constructor(private orderProfitService: OrderProfitService, private itemsService: ItemsService, private transloco: TranslocoService) {}

  ngOnInit(): void {
    this.loadWarehouses();
    if (this.warehouseId) {
      this.loadProfits();
    } else {
      this.errorMessage = this.transloco.translate('No warehouse selected');
    }
  }

  loadProfits(page = this.currentPage): void {
    if (!this.warehouseId) return;

    this.loading = true;
    this.errorMessage = '';
    this.orderProfitService.getProfitsByWarehouseMonthYear(
      this.warehouseId,
      this.selectedMonth,
      this.selectedYear,
      page,
      this.perPage
    ).subscribe({
      next: (response) => {
        this.applyResponse(response);
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.errorMessage = this.transloco.translate('Unable to load order profits');
      }
    });
  }

  syncProfits(): void {
    if (!this.warehouseId || this.syncing) return;

    this.syncing = true;
    this.errorMessage = '';
    this.orderProfitService.syncProfitsByWarehouse(this.warehouseId).subscribe({
      next: () => {
        this.syncing = false;
        this.loadProfits(1);
      },
      error: () => {
        this.syncing = false;
        this.errorMessage = this.transloco.translate('Unable to recalculate profits');
      }
    });
  }

  changePage(page: number): void {
    if (page < 1 || page > this.lastPage || page === this.currentPage) return;
    this.loadProfits(page);
  }

  applyFilters(): void {
    this.loadProfits(1);
  }

  private loadWarehouses(): void {
    this.itemsService.getWearHouses().subscribe({
      next: (response: any) => this.warehouses = response?.warehouse ?? response?.data ?? response ?? [],
      error: () => this.warehouses = []
    });
  }

  get rowRevenue(): (row: OrderProfitRow) => number {
    return row => this.numberFrom(row, ['gross_sales', 'revenue', 'total_amount', 'total', 'selling_price', 'sale_price']);
  }

  get rowCost(): (row: OrderProfitRow) => number {
    return row => this.numberFrom(row, ['products_cost', 'cost', 'total_cost', 'cost_price', 'purchase_price']);
  }

  get rowProfit(): (row: OrderProfitRow) => number {
    return row => {
      const value = row.net_profit ?? row.profit;
      if (value !== undefined && value !== null && value !== '') {
        const parsed = Number(String(value).replace(/,/g, ''));
        return Number.isNaN(parsed) ? 0 : parsed;
      }
      return this.rowRevenue(row) - this.rowCost(row) - this.rowDeductions(row);
    };
  }

  get rowDeductions(): (row: OrderProfitRow) => number {
    return row => this.numberFrom(row, ['invoice_discount']) + this.numberFrom(row, ['partial_deduction']) + this.numberFrom(row, ['other_deductions']);
  }

  private applyResponse(response: any): void {
    const payload = response?.data && !Array.isArray(response.data) ? response.data : response;
    const rows = Array.isArray(response) ? response : response?.data ?? payload?.data ?? payload?.profits ?? response?.profits ?? [];
    this.profits = Array.isArray(rows) ? rows : [];

    this.currentPage = Number(response?.current_page ?? payload?.current_page ?? 1);
    this.lastPage = Math.max(1, Number(response?.last_page ?? payload?.last_page ?? 1));
    this.totalRecords = Number(response?.total ?? payload?.total ?? this.profits.length);

    const summary = response?.summary ?? payload?.summary ?? response?.totals ?? payload?.totals ?? response;
    this.totalRevenue = this.numberFrom(summary, ['total_gross_sales', 'gross_sales', 'total_revenue', 'revenue', 'total_sales', 'sales']) || this.profits.reduce((sum, row) => sum + this.rowRevenue(row), 0);
    this.totalCost = this.numberFrom(summary, ['total_products_cost', 'products_cost', 'total_cost', 'cost', 'total_expenses']) || this.profits.reduce((sum, row) => sum + this.rowCost(row), 0);
    this.netProfit = this.numberFrom(summary, ['total_net_profit', 'net_profit', 'total_profit', 'profit']) || this.profits.reduce((sum, row) => sum + this.rowProfit(row), 0);
  }

  private numberFrom(source: any, keys: string[]): number {
    for (const key of keys) {
      const value = source?.[key];
      const parsed = typeof value === 'string' ? Number(value.replace(/,/g, '')) : Number(value);
      if (!Number.isNaN(parsed) && value !== null && value !== undefined && value !== '') return parsed;
    }
    return 0;
  }
}
